import { useEffect, useState } from 'react';
import { Card, Checkbox, Space, Switch, Table } from 'antd';
import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants.js';
import {
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { isNullOrUndefined, isNumeric, sumArray } from '../community-hats-js-library/utils/generalFunctions.js';
import { dateFormatter, getColorByLocation, getColorForPeriod, getPeriodLabel } from '../utils/chartFunctions.js';
import { CustomCircularProgressBar } from '../community-hats-js-library/components/WearableCoverageViewer.js';
import {
  CheckCircleTwoTone,
  ClockCircleOutlined,
  ClockCircleTwoTone,
  QuestionCircleOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import { formatInStudyLocationToCustom } from '../community-hats-js-library/utils/dateFunctions.js';
import Plot from 'react-plotly.js';
import Plotly from 'plotly.js-basic-dist';
import { sum } from 'd3';
import { RefContextLanguage } from '../providers/LanguageProvider.js';
import { tValue } from '../utils/translationFunctions.js';

export const WearableCoverageChart = ({ location, data }) => {
  const i18n = RefContextLanguage();

  return isNullOrUndefined(data) ? (
    <div></div>
  ) : (
    <Card
      headStyle={{ backgroundColor: 'var(--primary-color-3)', width: '50vh' }}
      className="no-bottom-margin"
      type="inner"
      title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>{i18n.t('currentCoverage20DayWindow')}</span>}
    >
      {isNullOrUndefined(location) || location === libCon.ATF_ALL_LOCATIONS || !(location in data) ? (
        <div className="verticalSection">
          <h3 style={{ color: 'black', marginTop: '5px', marginBottom: '0px' }}>{i18n.t('allLocations')}</h3>
          <CustomCircularProgressBar
            percentage={data[libCon.ATF_ALL_LOCATIONS][libCon.ATF_WEARABLE_TOTAL_COVERAGE]}
            updatingEmail={false}
            fontSize="20px"
            size="medium"
          />
        </div>
      ) : (
        <div className="verticalSection">
          <h3 style={{ color: 'black', marginTop: '5px', marginBottom: '0px' }}>{i18n.t('total')}</h3>
          <CustomCircularProgressBar
            percentage={data[location][libCon.ATF_WEARABLE_TOTAL_COVERAGE]}
            updatingEmail={false}
            fontSize="20px"
            size="medium"
          />
        </div>
      )}

      {isNullOrUndefined(location) || location === libCon.ATF_ALL_LOCATIONS || !(location in data) ? (
        <div className="horizontalSection" style={{ justifyContent: 'space-evenly' }}>
          {libCon.AREAS.map((area) => (
            <div key={area} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <h4 style={{ color: 'black', marginTop: '5px', marginBottom: '2px' }}>{tValue(i18n, area)}</h4>
              <h4 style={{ color: 'white', marginTop: '0px', marginBottom: '2px', padding: '0px' }}>{`--`}</h4>
              <CustomCircularProgressBar
                percentage={data[area][libCon.ATF_WEARABLE_TOTAL_COVERAGE]}
                updatingEmail={false}
              />
            </div>
          ))}{' '}
        </div>
      ) : (
        <div className="horizontalSection" style={{ justifyContent: 'space-evenly' }}>
          {/* Night */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h4 style={{ color: 'black', marginTop: '5px', marginBottom: '2px' }}>{i18n.t('night')}</h4>
            <h4 style={{ color: 'black', marginTop: '0px', marginBottom: '2px', padding: '0px' }}>{`00:00-6:00`}</h4>
            <CustomCircularProgressBar
              percentage={data[location][libCon.ATF_WEARABLE_NIGHT_COVERAGE]}
              updatingEmail={false}
            />
          </div>
          {/* Morning */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h4 style={{ color: 'black', marginTop: '5px', marginBottom: '0px', padding: '0px' }}>{i18n.t('morning')}</h4>
            <h4 style={{ color: 'black', marginTop: '0px', marginBottom: '2px', padding: '0px' }}>{`6:00-12:00`}</h4>
            <CustomCircularProgressBar
              percentage={data[location][libCon.ATF_WEARABLE_MORNING_COVERAGE]}
              updatingEmail={false}
            />
          </div>

          {/* Afternoon */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h4 style={{ color: 'black', marginTop: '5px', marginBottom: '0px', padding: '0px' }}>{i18n.t('afternoon')}</h4>
            <h4 style={{ color: 'black', marginTop: '0px', marginBottom: '2px', padding: '0px' }}>{`12:00-18:00`}</h4>
            <CustomCircularProgressBar
              percentage={data[location][libCon.ATF_WEARABLE_AFTERNOON_COVERAGE]}
              updatingEmail={false}
            />
          </div>

          {/* Evening */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h4 style={{ color: 'black', marginTop: '5px', marginBottom: '2px' }}>{i18n.t('evening')}</h4>
            <h4 style={{ color: 'black', marginTop: '0px', marginBottom: '2px', padding: '0px' }}>{`18:00-00:00`}</h4>
            <CustomCircularProgressBar
              percentage={data[location][libCon.ATF_WEARABLE_EVENING_COVERAGE]}
              updatingEmail={false}
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export const WearableCoverageHistoricChart = ({ location, data, domain, ticks }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${formatInStudyLocationToCustom(label, 'MMM d')}`}</p>
          <div className="verticalSection">
            {payload.map((val) => (
              <p key={val.name} style={{ margin: '0px' }}>{`${val.name}: ${val.value.toFixed(1)}%`}</p>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const i18n = RefContextLanguage();

  const [processedData, setProcessedData] = useState(null);
  const [chartKeys, setChartKeys] = useState([]);
  const [isLocationView, setIsLocationView] = useState(true);

  useEffect(() => {
    if (!isNullOrUndefined(data)) {
      // Determine if showing location view or period view
      const showingLocationView = isNullOrUndefined(location) || location === libCon.ATF_ALL_LOCATIONS;
      setIsLocationView(showingLocationView);

      if (showingLocationView) {
        // Location view: show all locations from ATF_ALL_LOCATIONS data
        setChartKeys([libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS]);
        // Convert coverage values to percentages
        setProcessedData(
          data[libCon.ATF_ALL_LOCATIONS].map((ob) => {
            const newOb = { ...ob };
            // Convert all coverage values to percentages
            [libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS].forEach((key) => {
              if (newOb[key] !== null && newOb[key] !== undefined) {
                newOb[key] = newOb[key] * 100;
              }
            });
            return newOb;
          })
        );
      } else {
        // Period view: show all periods for the selected location
        const periods = [libCon.MORNING, libCon.AFTERNOON, libCon.EVENING, libCon.NIGHT];
        setChartKeys(periods);
        // Convert coverage values to percentages
        setProcessedData(
          data[location].map((ob) => {
            const newOb = { ...ob };
            periods.forEach((period) => {
              if (newOb[period] !== null && newOb[period] !== undefined) {
                newOb[period] = newOb[period] * 100;
              }
            });
            return newOb;
          })
        );
      }
    }
  }, [data, location]);

  const getColor = (key) => {
    if (isLocationView) {
      return getColorByLocation(key);
    } else {
      return getColorForPeriod(location, key);
    }
  };

  const getLegendName = (key) => {
    if (isLocationView) {
      return tValue(i18n, key);
    } else {
      return getPeriodLabel(key, i18n);
    }
  };

  return isNullOrUndefined(processedData) ? (
    <div></div>
  ) : (
    <div>
      <h3 style={{ marginBottom: '1px' }}>{i18n.t('wearableCoverageLast90Days')}</h3>

      <ResponsiveContainer width={800} height={350}>
        <LineChart data={processedData} margin={{ top: 20, right: 30, bottom: 5 }} barCategoryGap="1%">
          <CartesianGrid stroke="#777777" strokeDasharray="3 3" />
          <XAxis
            dataKey={locCon.DATE}
            type="number"
            scale="time"
            tickFormatter={dateFormatter}
            domain={domain}
            ticks={ticks}
            tick={{ fontSize: 16, fill: '#ffffff' }}
          />
          <YAxis stroke="#ffffff" tick={{ fontSize: 16, fill: '#ffffff' }} domain={[0, 100]} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ color: '#ffffff' }} formatter={(value) => getLegendName(value)} />

          {chartKeys.map((key) => (
            <Line key={key} dataKey={key} name={getLegendName(key)} fill={getColor(key)} stroke={getColor(key)} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const HouseVisitChart = ({ location, data, domain, ticks }) => {
  const i18n = RefContextLanguage();

  const [isLines, setIsLines] = useState(true);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${formatInStudyLocationToCustom(label, 'MMM d')}`}</p>
          <div className="verticalSection">
            {payload.map((val) => (
              <p key={val.name} style={{ margin: '0px' }}>{`${val.name}: ${val.value}`}</p>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const [processedData, setProcessedData] = useState(null);

  useEffect(() => {
    if (!isNullOrUndefined(data)) {
      setProcessedData(
        data.map((ob) => {
          return { ...ob, [libCon.ATF_ALL_LOCATIONS]: sum(libCon.AREAS.map((ar) => ob[ar])) };
        })
      );
    }
  }, [data]);

  const [includeAll, setIncludeAll] = useState(false);
  const [areas, setAreas] = useState(libCon.AREAS);

  useEffect(() => {
    if (includeAll) setAreas([libCon.ATF_ALL_LOCATIONS, ...libCon.AREAS]);
    else setAreas([...libCon.AREAS]);
  }, [includeAll]);

  return isNullOrUndefined(processedData) ? (
    <div></div>
  ) : (
    <div>
      <h3 style={{ marginBottom: '1px' }}>{i18n.t('houseVisitedLast20Days')} </h3>
      <Space size={'large'}>
        <Switch checkedChildren={i18n.t('lines')} unCheckedChildren={i18n.t('bars')} checked={isLines} onChange={(b, e) => setIsLines(b)} />
        <Checkbox
          checked={includeAll}
          onChange={(e) => setIncludeAll(e.target.checked)}
          style={{ color: 'var(--text-color)', fontSize: '2vh' }}
        >
          {i18n.t('includeAll')}
        </Checkbox>
      </Space>

      <ResponsiveContainer width={800} height={350}>
        {isLines ? (
          <LineChart data={processedData} margin={{ top: 20, right: 30, bottom: 5 }} barCategoryGap="1%">
            <CartesianGrid stroke="#777777" strokeDasharray="3 3" />
            <XAxis
              dataKey={locCon.DATE}
              type="number"
              scale="time"
              tickFormatter={dateFormatter}
              domain={domain}
              ticks={ticks}
              tick={{ fontSize: 16, fill: '#ffffff' }}
            />
            <YAxis stroke="#ffffff" tick={{ fontSize: 16, fill: '#ffffff' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#ffffff' }} />

            {areas
              .filter((l) => isNullOrUndefined(location) || location === libCon.ATF_ALL_LOCATIONS || l === location)
              .map((area) => (
                <Line dataKey={area} fill={getColorByLocation(area)} stroke={getColorByLocation(area)} />
              ))}
          </LineChart>
        ) : (
          <BarChart data={processedData} margin={{ top: 20, right: 30, bottom: 5 }} barCategoryGap="1%">
            <CartesianGrid stroke="#777777" strokeDasharray="3 3" />
            <XAxis
              dataKey={locCon.DATE}
              type="number"
              scale="time"
              tickFormatter={dateFormatter}
              domain={domain}
              ticks={ticks}
              tick={{ fontSize: 16, fill: '#ffffff' }}
            />
            <YAxis stroke="#ffffff" tick={{ fontSize: 16, fill: '#ffffff' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: '#ffffff' }} />

            {areas
              .filter((l) => isNullOrUndefined(location) || location === libCon.ATF_ALL_LOCATIONS || l === location)
              .map((area) => (
                <Bar key={area} dataKey={area} fill={getColorByLocation(area)} />
              ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export const StatusTable = ({ location, data }) => {
  const i18n = RefContextLanguage();

  const renderValue = (val, record, color) => {
    const active =
      !isNullOrUndefined(location) && location !== libCon.ATF_ALL_LOCATIONS && record[libCon.ATF_LOCATION] === location;

    if (isNullOrUndefined(val)) {
      return (
        <p
          style={{ position: 'relative', margin: 0, padding: '0px', zIndex: 1, fontWeight: active ? 'bold' : 'normal' }}
        >
          0 (0%)
        </p>
      );
    }

    let total = sumArray(Object.values(record).filter((v) => isNumeric(v)));
    let per = total === 0 ? 0 : Math.round((100 * val) / total);

    return (
      <div style={{ position: 'relative' }}>
        <div
          style={{
            width: `${per}%`,
            height: '100%',
            backgroundColor: color, // green with transparency
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 0,
            transition: 'width 0.3s ease',
          }}
        />
        <p
          style={{ position: 'relative', margin: 0, padding: '0px', zIndex: 1, fontWeight: active ? 'bold' : 'normal' }}
        >
          {`${val} (${per}%)`}
        </p>
      </div>
    );
  };

  const columns = [
    {
      title: <p>{i18n.t('location')}</p>,
      dataIndex: libCon.ATF_LOCATION,
      key: 1,
      align: 'center',
      width: 100,
      render: (val, record) => (
        <p
          style={{
            margin: 0,
            padding: '0px',
            fontWeight:
              !isNullOrUndefined(location) &&
              location !== libCon.ATF_ALL_LOCATIONS &&
              record[libCon.ATF_LOCATION] === location
                ? 'bold'
                : 'normal',
          }}
        >
          {tValue(i18n, val)}
        </p>
      ),
    },
    {
      title: (
        <p>
          <CheckCircleTwoTone style={{ fontSize: 18 }} twoToneColor="#52c41a" /> {i18n.t('statusOk')}{' '}
        </p>
      ),
      dataIndex: libCon.DEVICE_STATUS_OK,
      key: 2,
      align: 'center',
      width: 100,
      render: (val, record) =>
        renderValue(
          val,
          record,
          !isNullOrUndefined(location) &&
            location !== libCon.ATF_ALL_LOCATIONS &&
            record[libCon.ATF_LOCATION] === location
            ? 'var(--success-color-1)'
            : 'var(--success-color-2-transparent)'
        ),
    },
    {
      title: (
        <p>
          <ClockCircleOutlined style={{ fontSize: 18 }} /> {i18n.t('waitingForCollection')}{' '}
        </p>
      ),
      dataIndex: libCon.DEVICE_STATUS_WAITING_COLLECTION,
      key: 2,
      align: 'center',
      width: 100,
      render: (val, record) =>
        renderValue(
          val,
          record,
          !isNullOrUndefined(location) &&
            location !== libCon.ATF_ALL_LOCATIONS &&
            record[libCon.ATF_LOCATION] === location
            ? 'var(--success-color-1)'
            : 'var(--success-color-2-transparent)'
        ),
    },
    {
      title: (
        <p>
          <ClockCircleTwoTone style={{ fontSize: 18 }} twoToneColor="#e91822" /> {i18n.t('loosingData')}{' '}
        </p>
      ),
      dataIndex: libCon.DEVICE_STATUS_WAITING_COLLECTION_LOOSING_DATA,
      key: 2,
      align: 'center',
      width: 100,
      render: (val, record) =>
        renderValue(
          val,
          record,
          !isNullOrUndefined(location) &&
            location !== libCon.ATF_ALL_LOCATIONS &&
            record[libCon.ATF_LOCATION] === location
            ? 'var(--warning-color-1)'
            : 'var(--warning-color-1-transparent)'
        ),
    },
    {
      title: (
        <p>
          <WarningTwoTone style={{ fontSize: 18 }} twoToneColor="#e91822" /> {i18n.t('errorInCollection')}{' '}
        </p>
      ),
      dataIndex: libCon.DEVICE_STATUS_ERROR_IN_COLLECTION,
      key: 2,
      align: 'center',
      width: 100,
      render: (val, record) =>
        renderValue(
          val,
          record,
          !isNullOrUndefined(location) &&
            location !== libCon.ATF_ALL_LOCATIONS &&
            record[libCon.ATF_LOCATION] === location
            ? 'var(--warning-color-1)'
            : 'var(--warning-color-1-transparent)'
        ),
    },
    {
      title: (
        <p>
          <QuestionCircleOutlined style={{ fontSize: 18 }} /> {i18n.t('other')}{' '}
        </p>
      ),
      dataIndex: libCon.ATF_OTHER,
      key: 2,
      align: 'center',
      width: 100,
      render: (val, record) => renderValue(val, record, 'var(--warning-color-1-transparent)'),
    },
  ];

  return (
    <div className="no-header-padding" style={{ marginBottom: '1vh' }}>
      <h3 style={{ marginBottom: '1vh', marginTop: '1px' }}>{i18n.t('generalStatus')} </h3>
      <Table dataSource={data} columns={columns} size="small" pagination={false} />
    </div>
  );
};

export const LoosingData = ({ location, data, userType }) => {
  const i18n = RefContextLanguage();

  const titles = {
    [locCon.MORE_30]: ' > 30',
    [locCon.MORE_20]: '21-30',
    [locCon.MORE_10]: '11-20',
    [locCon.MORE_0]: '1-10',
  };

  const columns = [
    {
      title: <p>{i18n.t('name')}</p>,
      dataIndex: libCon.ATF_NAME,
      key: 1,
      align: 'center',
      width: 100,
      render: (val, record) => val,
    },
    {
      title: <p>{i18n.t('sewaId')}</p>,
      dataIndex: libCon.ATF_SEWA_ID,
      key: 2,
      align: 'center',
      width: 100,
      render: (val, record) => val,
    },
    {
      title: <p>{i18n.t('lostDataDays')}</p>,
      dataIndex: libCon.ATF_DAYS_UNTIL_EXPIRE,
      key: 3,
      align: 'center',
      width: 100,
      render: (val, record) => (
        <p style={{ color: 'var(--warning-color-1)', margin: 0, padding: '0px', fontWeight: 'bold' }}>
          {Math.ceil(-1 * val)}
        </p>
      ),
    },
  ];

  const [dataKey, setDataKey] = useState(() =>
    isNullOrUndefined(location) || !(location in data) ? libCon.ATF_ALL_LOCATIONS : location
  );

  useEffect(() => {
    setDataKey(isNullOrUndefined(location) || !(location in data) ? libCon.ATF_ALL_LOCATIONS : location);
  }, [location, data]);

  return (
    <div>
      <h3 style={{ marginBottom: '1vh', marginTop: '2vh' }}>{i18n.t('participantsWithLostData')} </h3>
      <h6
        style={{ marginBottom: '1vh', marginTop: '0.5vh' }}
      >{`${i18n.t('total')}: ${sumArray(Object.keys(titles).map((k) => data[dataKey][k].length))}`}</h6>

      <div className="horizontalSection">
        {Object.keys(titles).map((k) => (
          <div
            key={k}
            className="no-header-padding"
            style={{ marginBottom: '1vh', marginLeft: '10px', marginRight: '10px' }}
          >
            <h4 style={{ marginBottom: '1vh', marginTop: '0.5vh' }}>{titles[k]}</h4>
            <h6 style={{ marginBottom: '1vh', marginTop: '0.5vh' }}>{`${i18n.t('total')}: ${data[dataKey][k].length}`}</h6>

            <Table
              dataSource={data[dataKey][k]}
              columns={columns.filter((c) => userType === locCon.USER_TYPE_SEWA || c.dataIndex !== libCon.ATF_NAME)}
              size="small"
              pagination={false}
              scroll={{ y: window.innerHeight * 0.3 }}
              style={{ width: '100%', maxWidth: '22vw', display: 'inline-block', verticalAlign: 'top' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export const CollectionDurationChart = ({ data, location }) => {
  const i18n = RefContextLanguage();

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let newData = Object.keys(data).map((key) => {
      let color = getComputedStyle(document.documentElement)
        .getPropertyValue(getColorByLocation(key).replace('var(--', '--').replace(')', ''))
        .trim();

      return {
        name: key,
        type: 'box',
        // boxpoints: 'all',
        y: data[key],
        marker: {
          color: color, // fill color
        },
        line: {
          color: color, // border color
        },
      };
    });

    if (location !== libCon.ATF_ALL_LOCATIONS && location in data) newData = newData.filter((d) => d.name === location);

    setChartData(newData);
  }, [data, location]);

  return (
    <div>
      <h3 style={{ marginBottom: '1px' }}>{i18n.t('averageCollectionDurationMinutes')}</h3>
      <Plot
        plotly={Plotly}
        data={chartData}
        layout={{
          width: 800,
          height: 350,
          margin: { t: 40, r: 30, b: 40, l: 60 },
          paper_bgcolor: 'rgba(0,0,0,0)', // Transparent outer background
          plot_bgcolor: 'rgba(0,0,0,0)', // Transparent plot area
          xaxis: {
            color: 'white', // X-axis label and ticks color
            tickfont: { size: 16, color: 'white' },
          },
          yaxis: {
            color: 'white', // Y-axis label and ticks color
            tickfont: { size: 16, color: 'white' },
          },
          legend: {
            font: {
              color: 'white', // Legend text white
              size: 14,
            },
          },
          hoverlabel: {
            bgcolor: 'rgba(0,0,0,0)', // transparent background for hover box
            font: {
              color: 'white', // optional: make the text white if needed
            },
          },
        }}
        config={{ displayModeBar: false }}
      />
    </div>
  );
};
